import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLayout } from '../../../../providers/Layout.provider';
import { useBank } from '../../../../providers/Bank.provider';
import { useToken } from '../../../../providers/Token.provider';
import styles from './BankLoginList.module.scss';

type BankLoginListProps = {
  selectedAccountService: number | null;
  setSelectedAccountService: (val: number | null) => void;
};

const { Button, Accordion } = vp;

type FC = FunctionComponent<BankLoginListProps>;

const BankLoginList: FC = (props: BankLoginListProps) => {
  const { selectedAccountService, setSelectedAccountService } = props;
  const { setBankingType } = useBank();
  const { token } = useToken();
  const { color } = useLayout();
  const { t } = useTranslation('translations', { keyPrefix: 'BankConnection' });
  const { setFinancialInstitution, SubFiniancialIntistutions, FinancialInstitution } = useBank();
  const [currentInstitutionNum, setCurrentInstitutionNum] = useState(4);
  const [isMobile, setIsMobile] = useState(false);
  const numOfSubInstitutions = Number(SubFiniancialIntistutions?.length);
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const isBankTypeKnown = token?.AccountSelectionMethod !== 'any' && token?.AccountHolderType !== '';

  useEffect(() => {
    setSelectedAccountService(null);
  }, [SubFiniancialIntistutions]);

  useEffect(() => {
    if (isMobile) {
      setCurrentInstitutionNum(3);
    } else {
      setCurrentInstitutionNum(4);
    }
  }, [isMobile]);

  useEffect(() => {
    setIsMobile(window.innerWidth <= 600);
  }, [numOfSubInstitutions]);

  const SelectedItem = (financialInstitution: vp.FinancialInstitutionInfo) => {
    setFinancialInstitution(financialInstitution);

    if (financialInstitution.OnlineConnectionEnabled === '0') {
      setBankingType('manual');
    } else if (token?.AccountSelectionMethod === 'online') {
      setBankingType('online');
    } else if (token?.AccountSelectionMethod === 'manual') {
      setBankingType('manual');
    }
  };

  const handlers = {
    selectBankAccount: (values: { SelectedBankId: string }) => {
      const SelectedFinancialInstitution = SubFiniancialIntistutions?.find(
        (institution) => institution.ExternalFIID === values.SelectedBankId,
      );
      if (SelectedFinancialInstitution) SelectedItem(SelectedFinancialInstitution);
    },
  };

  const chooseService = () => (
    <>
      {!isAccordionOpen && selectedAccountService !== null ? (
        <div className={styles.selectedService}>
          <span>{FinancialInstitution?.FIName}</span>
        </div>
      ) : null}
    </>
  );

  return (
    <Accordion
      title={`${isBankTypeKnown ? '3' : '4'}. ${t('selectServiceTitle')}`}
      iconColor={token?.DarkMode ? 'primary-light' : 'primary-dark'}
      iconSize="xsm"
      variant="box"
      titlePadding="0px"
      open={isAccordionOpen}
      onClick={() => {
        setIsAccordionOpen(!isAccordionOpen);
      }}
      subBody={chooseService()}
      body={
        <>
          <div className={styles.subListContainer}>
            {SubFiniancialIntistutions?.slice(0, currentInstitutionNum).map((bank, index) => {
              const topCABankID = ['1', '2', '3', '4', '6'];
              const isTopBank = topCABankID.includes(String(bank.FinancialInstitutionID));

              return (
                <div className={styles.bankContainer} key={bank.ExternalFIID}>
                  <Button
                    id={`sub-institution-${index}`}
                    label={bank.FIName}
                    color={
                      selectedAccountService === index ? (vp.getLuminance(color) > 0.5 ? 'primary' : 'none-light') : ''
                    }
                    background={selectedAccountService === index ? color : '#FFFFFF'}
                    textColor={
                      selectedAccountService === index
                        ? vp.getLuminance(color) > 0.5
                          ? '#2D2D2D'
                          : '#FFFFFF'
                        : '#2D2D2D'
                    }
                    textAlign="left"
                    width="100%"
                    height="40px"
                    fontUnit="px"
                    textSize={15}
                    fontWeight={400}
                    icon="check"
                    hasCustomIcon={selectedAccountService === index ? false : !!isTopBank}
                    isIconVisible={selectedAccountService === index ? true : !!isTopBank}
                    customIconImage={
                      selectedAccountService !== index
                        ? String(bank.FinancialInstitutionID) === '1'
                          ? '/images/banks-circular-logos/CA/BMO.svg'
                          : String(bank.FinancialInstitutionID) === '2'
                            ? '/images/banks-circular-logos/CA/Scotia.svg'
                            : String(bank.FinancialInstitutionID) === '3'
                              ? '/images/banks-circular-logos/CA/RBC.svg'
                              : String(bank.FinancialInstitutionID) === '4'
                                ? '/images/banks-circular-logos/CA/TD.svg'
                                : String(bank.FinancialInstitutionID) === '6'
                                  ? '/images/banks-circular-logos/CA/CIBC.svg'
                                  : ''
                        : ''
                    }
                    iconLocation="left"
                    iconWidth="16px"
                    iconHeight="16px"
                    round
                    noShadow
                    onClick={() => {
                      setSelectedAccountService(index);
                      const sanatizedSelectedBank = bank.ExternalFIID.trim();
                      const bankingValuesSanitized = { ...bank, SelectedBankId: sanatizedSelectedBank };
                      handlers.selectBankAccount(bankingValuesSanitized);
                    }}
                  />
                </div>
              );
            })}
          </div>

          {currentInstitutionNum < numOfSubInstitutions && (
            <div className={styles.viewAllButton}>
              <Button
                id="view-all-sub-financial-list"
                label="View All"
                width="60px"
                fontUnit="px"
                background="transparent"
                noShadow
                noPadding
                textSize={14}
                fontWeight={700}
                onClick={() => {
                  setCurrentInstitutionNum(numOfSubInstitutions);
                }}
              />
            </div>
          )}
        </>
      }
    />
  );
};

export default BankLoginList;
